import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import AbToolDialog from "@/components/AbToolDialog";
import PagedPdfViewer from "@/components/PagedPdfViewer";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Save, Plus, Trash2, ArrowUp, ArrowDown, FileCode2, RefreshCw,
  Loader2, CheckCircle2, FileText, Copy, Eye, Code2, PauseCircle, ChevronRight, LocateFixed, GripVertical, ExternalLink, AlertCircle,
} from "lucide-react";

const EMPTY_ENTRY = () => ({
  date: "", page: "", bispage: "", position: "a", content: "",
  content_raw: "", behoerde: "", fundstelle: "", fna: "",
});

// Pflichtfelder für den OK-Split (muss zur Backend-Validierung passen).
const FIELD_LABEL_DE = { page: "von-Seite", bispage: "bis-Seite", content: "Titel", position: "Position" };

// "Heftangaben" ist der Sonder-Eintrag (Kopfdaten des Heftes) — am Titel erkannt.
const isHeftangaben = (en) => (en?.content || "").trim().toLowerCase().startsWith("heftangaben");
// "Inhaltsverzeichnis" — Sonder-Eintrag, Standard-Position "b".
const isInhalt = (en) => (en?.content || "").trim().toLowerCase().startsWith("inhaltsverzeichnis");

// Fehlende Pflichtfelder eines Eintrags (Heftangaben ausgenommen) für den OK-Split.
const entryMissing = (en) => {
  if (isHeftangaben(en)) return [];
  const m = [];
  if (en.page === "" || en.page == null) m.push("page");
  if (en.bispage === "" || en.bispage == null) m.push("bispage");
  if (!(en.content || "").trim()) m.push("content");
  if (!String(en.position ?? "").trim()) m.push("position");
  return m;
};

// "Heftangaben" steht immer ganz oben als erster Eintrag.
const sortHeftangabenFirst = (arr) => {
  const idx = (arr || []).findIndex(isHeftangaben);
  if (idx <= 0) return arr || [];
  const c = [...arr];
  const [h] = c.splice(idx, 1);
  c.unshift(h);
  return c;
};

async function downloadBlob(url, filename) {
  const r = await api.get(url, { responseType: "blob" });
  const href = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = href; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(href);
}

export default function DocumentEditor({ docId, jump, onSaved, readOnly = false, actions = null }) {
  const isDesktop = useIsDesktop();
  const [doc, setDoc] = useState(null);
  const [entries, setEntries] = useState([]);
  const [meta, setMeta] = useState({});
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfError, setPdfError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("editor");
  const [xml, setXml] = useState("");
  const [openIdx, setOpenIdx] = useState(null);
  const [pageTarget, setPageTarget] = useState(null);
  const [autoJump, setAutoJump] = useState(true);
  const [cleanKey, setCleanKey] = useState("");
  const [xmlClean, setXmlClean] = useState("");
  const pollRef = useRef(null);

  const applyDoc = useCallback((d) => {
    setDoc(d);
    // Default-Position: "b" für Inhaltsverzeichnis, sonst "a" (außer Heftangaben-Sondereintrag).
    const withPos = (d.entries || []).map((en) => {
      if (isHeftangaben(en) || en.position) return en;
      return { ...en, position: isInhalt(en) ? "b" : "a" };
    });
    const sorted = sortHeftangabenFirst(withPos);
    const m = {
      nummer: d.nummer || "", jahr: d.jahr || "",
      page_start: d.page_start ?? "", page_end: d.page_end ?? "", link: d.link || "",
    };
    setEntries(sorted);
    setMeta(m);
    // Sauberer Zustand nach Laden/Speichern → Speichern-Button erst bei echter Änderung aktiv.
    setCleanKey(JSON.stringify({ entries: sorted, meta: m }));
  }, []);

  const fetchDoc = useCallback(async () => (await api.get(`/documents/${docId}`)).data, [docId]);

  // load document
  useEffect(() => {
    let active = true;
    setDoc(null); setXml(""); setTab("editor"); setOpenIdx(null); setPageTarget(null);
    fetchDoc().then((d) => active && applyDoc(d)).catch(() => toast.error("Dokument nicht gefunden"));
    return () => { active = false; };
  }, [docId, fetchDoc, applyDoc]);

  // Sprung zu einer bestimmten PDF-Seite (Volltext-Suche) → PDF-Viewer navigiert per Anker.
  useEffect(() => {
    if (jump) {
      setTab("editor");
      setPageTarget({ p: (jump.page || 0) + 1, nonce: jump.nonce || Date.now() });
    }
  }, [jump]);

  // poll while processing
  useEffect(() => {
    if (!doc) return;
    if (doc.status === "processing") {
      pollRef.current = setInterval(async () => {
        const d = await fetchDoc();
        if (d.status !== "pending" && d.status !== "processing") {
          applyDoc(d);
          clearInterval(pollRef.current);
          if (d.status === "ocr_done") toast.success("OCR-Analyse abgeschlossen");
          if (d.status === "error") toast.error("OCR-Analyse fehlgeschlagen");
          onSaved && onSaved();
        }
      }, 5000);
      return () => clearInterval(pollRef.current);
    }
  }, [doc?.status, fetchDoc, applyDoc, onSaved]); // eslint-disable-line

  // Echtes PDF laden (Blob) → nativer Viewer, Text markierbar/kopierbar.
  useEffect(() => {
    let url = null; let cancelled = false;
    setPdfUrl(null); setPdfError(false);
    (async () => {
      try {
        const r = await api.get(`/documents/${docId}/pdf`, { responseType: "blob" });
        if (cancelled) return;
        url = URL.createObjectURL(r.data);
        setPdfUrl(url);
      } catch { if (!cancelled) setPdfError(true); }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [docId]);

  const pdfSrc = useMemo(() => {
    if (!pdfUrl) return "";
    const p = pageTarget?.p;
    return p ? `${pdfUrl}#page=${p}&zoom=page-width` : `${pdfUrl}#zoom=page-width`;
  }, [pdfUrl, pageTarget]);

  // Accordion: Titel anklicken → auf-/zuklappen; beim Aufklappen zur PDF-Seite springen.
  // Referenz für "PDF-Seite 1" ist die "von Seite" des Heftangaben-Eintrags (nicht die gedruckte
  // Seitenzahl). Physische PDF-Seite = Eintrag.vonSeite − Heftangaben.vonSeite + 1.
  const jumpToEntry = useCallback((en) => {
    const heft = entries.find(isHeftangaben);
    const base = parseInt(heft?.page, 10);
    const start = !isNaN(base) ? base : parseInt(meta.page_start, 10);
    const ep = parseInt(en?.page, 10);
    let p = 1;
    if (!isNaN(ep)) p = (!isNaN(start) && start > 0) ? Math.max(1, ep - start + 1) : Math.max(1, ep);
    setPageTarget({ p, nonce: Date.now() });
    setTab("editor");
  }, [entries, meta.page_start]);

  const toggleEntry = (i, en) => {
    if (openIdx === i) { setOpenIdx(null); return; }
    setOpenIdx(i);
    if (autoJump) jumpToEntry(en);
  };

  const loadXml = useCallback(async () => {
    try {
      const r = await api.get(`/documents/${docId}/export/xml`, { responseType: "text" });
      const s = typeof r.data === "string" ? r.data : JSON.stringify(r.data);
      setXml(s);
      setXmlClean(s);
    } catch { setXml("<!-- XML konnte nicht geladen werden -->"); }
  }, [docId]);

  const updateEntry = (i, f, v) => setEntries((e) => e.map((en, idx) => {
    if (idx !== i) return en;
    const next = { ...en, [f]: v };
    if (f === "content") {
      // Standard-Position: "a" für normale Einträge, "b" für das Inhaltsverzeichnis.
      if (isInhalt(next) && (en.position === "" || en.position === "a")) next.position = "b";
      else if (!isInhalt(next) && isInhalt(en) && en.position === "b") next.position = "a";
    }
    return next;
  }));
  const move = (i, dir) => setEntries((e) => {
    const j = i + dir; if (j < 0 || j >= e.length) return e;
    if (isHeftangaben(e[i]) || isHeftangaben(e[j])) return e; // Heftangaben bleibt oben
    const c = [...e]; [c[i], c[j]] = [c[j], c[i]]; return c;
  });
  const removeEntry = (i) => setEntries((e) => e.filter((_, idx) => idx !== i));
  const addEntry = () => setEntries((e) => [...e, EMPTY_ENTRY()]);

  // Drag & Drop: Einträge am Griff anfassen und verschieben (Heftangaben bleibt oben).
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const reorder = (from, to, after) => setEntries((e) => {
    if (from == null || from < 0 || from >= e.length) return e;
    if (isHeftangaben(e[from])) return e;                 // Heftangaben nicht verschiebbar
    let insert = after ? to + 1 : to;
    const c = [...e];
    const [moved] = c.splice(from, 1);
    if (from < insert) insert -= 1;                       // Index nach Entnahme korrigieren
    insert = Math.max(0, Math.min(insert, c.length));
    if (c.length && isHeftangaben(c[0]) && insert <= 0) insert = 1; // nie über Heftangaben
    c.splice(insert, 0, moved);
    return c;
  });
  const onDragStart = (e, i) => {
    if (readOnly || isHeftangaben(entries[i])) { e.preventDefault(); return; }
    setDragIdx(i);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(i)); } catch {}
  };
  const onDragOverRow = (e, i) => {
    if (dragIdx == null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overIdx !== i) setOverIdx(i);
  };
  const onDropRow = (e, i) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const after = (e.clientY - rect.top) > rect.height / 2;
    reorder(dragIdx, i, after);
    setDragIdx(null); setOverIdx(null); setOpenIdx(null);
  };
  const onDragEnd = () => { setDragIdx(null); setOverIdx(null); };

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const payload = {
        entries: entries.map((en) => ({
          ...en,
          page: en.page === "" ? null : parseInt(en.page, 10),
          bispage: en.bispage === "" ? null : parseInt(en.bispage, 10),
        })),
        nummer: meta.nummer || null,
        jahr: meta.jahr ? parseInt(meta.jahr, 10) : null,
        page_start: meta.page_start === "" ? null : parseInt(meta.page_start, 10),
        page_end: meta.page_end === "" ? null : parseInt(meta.page_end, 10),
        link: meta.link || null,
        ...extra,
      };
      const r = await api.put(`/documents/${docId}`, payload);
      applyDoc(r.data);
      const msg = extra.status === "fertig" ? "Als fertig markiert – Einträge in die OK gesplittet"
        : extra.status === "zurueckgestellt" ? "Zurückgestellt" : "Gespeichert";
      toast.success(msg);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen",
        e?.response?.status === 422 ? { duration: 8000 } : undefined);
    }
    finally { setSaving(false); }
  };

  const reanalyze = async () => {
    try {
      await api.post(`/documents/${docId}/analyze`);
      setDoc((d) => ({ ...d, status: "processing" }));
      toast.success("OCR-Analyse gestartet");
    } catch { toast.error("Analyse fehlgeschlagen"); }
  };

  // XML-Modus: bearbeitetes XML zurückspeichern (optional zusätzlich Status setzen).
  const saveXml = async (extra = {}) => {
    setSaving(true);
    try {
      const r = await api.post(`/documents/${docId}/import/xml`, xml, {
        headers: { "Content-Type": "application/xml" },
      });
      let updated = r.data;
      if (extra.status) {
        updated = (await api.put(`/documents/${docId}`, { status: extra.status })).data;
      }
      applyDoc(updated);
      await loadXml();
      const msg = extra.status === "fertig" ? "Als fertig markiert"
        : extra.status === "zurueckgestellt" ? "Zurückgestellt" : "XML gespeichert";
      toast.success(msg);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "XML speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  if (!doc) return <div className="h-full flex items-center justify-center text-gray-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const processing = doc.status === "processing";
  const isPending = doc.status === "pending";
  // Speichern nur bei echter Änderung aktiv (Editor: entries/meta; XML: Textänderung).
  const dirty = JSON.stringify({ entries, meta }) !== cleanKey;
  const xmlDirty = xml !== xmlClean;
  const saveDisabled = saving || (tab === "xml" ? !xmlDirty : !dirty);

  return (
    <div className="h-full flex flex-col min-h-0 doc-editor-hc">
      {/* toolbar */}
      <div className="shrink-0 border-b border-gray-200 bg-white px-5 py-3 flex items-center gap-3 flex-wrap">
        {/* Steuerleiste über den Einträgen — linksbündig */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-md border border-gray-300 overflow-hidden">
            <button onClick={() => setTab("editor")} data-testid="tab-editor" title="Editor-Ansicht"
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 ${tab === "editor" ? "bg-[#0F4C81] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
              <Eye className="h-3.5 w-3.5" /> Editor
            </button>
            <button onClick={() => { setTab("xml"); loadXml(); }} data-testid="tab-xml" title="XML-Ansicht"
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 ${tab === "xml" ? "bg-[#0F4C81] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
              <Code2 className="h-3.5 w-3.5" /> XML
            </button>
          </div>
          {tab === "editor" && !readOnly && (
            <AbToolDialog docId={docId} onDone={(d) => { applyDoc(d); setOpenIdx(null); onSaved && onSaved(); }} />
          )}
          {tab === "xml" && (
            <Button variant="outline" size="sm" onClick={() => downloadBlob(`/documents/${docId}/export/xml`, doc.filename.replace(/\.pdf$/, ".xml"))} data-testid="editor-xml" title="XML herunterladen"><FileCode2 className="h-4 w-4 mr-1" /> XML</Button>
          )}
          {tab === "editor" && (
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer select-none" title="Beim Öffnen eines Eintrags automatisch zur PDF-Seite springen">
              <Switch checked={autoJump} onCheckedChange={setAutoJump} data-testid="autojump-toggle" />
              <span className="flex items-center gap-1"><LocateFixed className="h-3.5 w-3.5" /> Auto-Sprung</span>
            </label>
          )}
          {actions ? actions : (
            <>
              <Button size="sm" className="bg-[#0F4C81] hover:bg-[#0d3f6b] text-white" onClick={() => (tab === "xml" ? saveXml() : save())} disabled={saveDisabled} data-testid="editor-save" title={saveDisabled && !saving ? "Keine Änderungen zum Speichern" : "Änderungen speichern"}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Speichern
              </Button>
              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => (tab === "xml" ? saveXml({ status: "fertig" }) : save({ status: "fertig" }))} disabled={saving} data-testid="editor-review" title="Als fertig markieren – Einträge in die OK splitten"><CheckCircle2 className="h-4 w-4 mr-1" /> Fertig</Button>
              <Button size="sm" variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50" onClick={() => (tab === "xml" ? saveXml({ status: "zurueckgestellt" }) : save({ status: "zurueckgestellt" }))} disabled={saving} data-testid="editor-defer" title="Zurückstellen (bleibt in der PP)"><PauseCircle className="h-4 w-4 mr-1" /> Zurückstellen</Button>
            </>
          )}
        </div>
        {/* Spacer schiebt Dateiname/Status nach rechts (über das PDF) */}
        <div className="hidden lg:block flex-1" />
        {/* Dateiname/Status — rechts über dem PDF */}
        <div className="min-w-0 text-right">
          <div className="flex items-center gap-2 justify-end">
            <h2 className="font-heading text-base font-bold text-gray-900 truncate" data-testid="editor-filename">{doc.filename}</h2>
            <StatusBadge status={doc.status} testId="editor-status" />
          </div>
          <p className="text-xs text-gray-500">{(doc.blatt || "").toUpperCase()} · {doc.land} · {(doc.entries || []).length} Einträge</p>
        </div>
      </div>

      {tab === "xml" ? (
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          <div className="shrink-0 px-5 py-2 border-b border-gray-100 flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-widest text-gray-500">XML bearbeiten</span>
            <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(xml); toast.success("XML kopiert"); }} data-testid="xml-copy" title="XML in Zwischenablage kopieren"><Copy className="h-3.5 w-3.5 mr-1" /> Kopieren</Button>
          </div>
          <textarea
            value={xml}
            onChange={(e) => setXml(e.target.value)}
            readOnly={readOnly}
            spellCheck={false}
            className="flex-1 min-h-0 w-full resize-none overflow-auto bg-gray-900 text-gray-100 text-xs p-5 font-mono leading-relaxed outline-none border-0 focus:ring-0"
            data-testid="xml-viewer"
          />
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col lg:grid lg:grid-cols-2 overflow-y-auto lg:overflow-hidden">
          {/* Editor / Felder (LINKS) */}
          <div className="shrink-0 min-h-0 lg:overflow-y-auto p-5 bg-gray-100 lg:border-r border-gray-300">
            <fieldset disabled={readOnly} className="space-y-5 min-w-0 disabled:cursor-not-allowed">
            {processing && (
              <div className="flex items-center gap-2 rounded-md border border-sky-300 bg-sky-50 p-3 text-sm text-sky-900" data-testid="ocr-running">
                <Loader2 className="h-4 w-4 animate-spin" /> OCR-Analyse läuft — Einträge erscheinen automatisch.
              </div>
            )}
            {isPending && (
              <div className="rounded-md border border-gray-300 bg-white p-4 flex items-center justify-between gap-3" data-testid="ocr-pending">
                <div className="text-sm text-gray-700">Dieses Dokument wurde noch nicht per OCR analysiert.</div>
                <Button size="sm" className="bg-[#0F4C81] hover:bg-[#0d3f6b] text-white" onClick={reanalyze} data-testid="start-ocr" title="OCR-Analyse starten">
                  <RefreshCw className="h-4 w-4 mr-1" /> OCR-Analyse starten
                </Button>
              </div>
            )}
            {doc.status === "error" && doc.ocr_error && (
              <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900" data-testid="editor-error">
                OCR-Fehler: <span className="font-mono text-xs">{doc.ocr_error}</span>
              </div>
            )}

            <div className="bg-white border border-gray-300 rounded-lg p-4 shadow-sm">
              <h3 className="text-xs font-bold uppercase tracking-widest text-gray-700 mb-3">Heftangaben</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><Label className="text-xs">Nummer</Label><Input value={meta.nummer} onChange={(e) => setMeta({ ...meta, nummer: e.target.value })} className="font-bold" data-testid="meta-nummer" /></div>
                <div><Label className="text-xs">Jahr</Label><Input value={meta.jahr} onChange={(e) => setMeta({ ...meta, jahr: e.target.value })} className="font-bold" data-testid="meta-jahr" /></div>
                <div><Label className="text-xs">Startseite</Label><Input value={meta.page_start} onChange={(e) => setMeta({ ...meta, page_start: e.target.value })} className="font-bold" data-testid="meta-start" /></div>
                <div><Label className="text-xs">Endseite</Label><Input value={meta.page_end} onChange={(e) => setMeta({ ...meta, page_end: e.target.value })} className="font-bold" data-testid="meta-end" /></div>
              </div>
              <div className="mt-3"><Label className="text-xs">Link (Fundstelle Quelle)</Label>
                <div className="flex items-center gap-1.5">
                  <Input value={meta.link} onChange={(e) => setMeta({ ...meta, link: e.target.value })} className="font-bold" data-testid="meta-link" />
                  <a href={meta.link || undefined} target="_blank" rel="noopener noreferrer"
                    title={meta.link ? "Fundstelle in neuem Tab öffnen" : "Kein Link vorhanden"}
                    aria-disabled={!meta.link}
                    onClick={(e) => { if (!meta.link) e.preventDefault(); }}
                    data-testid="meta-link-open"
                    className={`shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-md border transition-colors ${meta.link ? "border-gray-400 bg-white text-[#0F4C81] hover:bg-[#0F4C81]/10 cursor-pointer" : "border-gray-200 bg-gray-50 text-gray-300 pointer-events-none"}`}>
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-widest text-gray-700">Gliederung ({entries.length})</h3>
              <Button size="sm" variant="outline" onClick={addEntry} data-testid="add-entry" title="Neuen Eintrag hinzufügen"><Plus className="h-4 w-4 mr-1" /> Eintrag</Button>
            </div>

            {entries.length === 0 && !processing && !isPending && (
              <div className="bg-white border border-dashed border-gray-400 rounded-lg p-8 text-center text-sm text-gray-600 shadow-sm">
                Noch keine Einträge. Analysieren Sie das Dokument oder fügen Sie manuell hinzu.
              </div>
            )}

            <div className="space-y-2">
            {entries.map((en, i) => {
              const open = openIdx === i;
              const heft = isHeftangaben(en);
              const miss = readOnly ? [] : entryMissing(en);
              const inv = (f) => miss.includes(f);
              const invalid = miss.length > 0;
              return (
              <div key={i}
                onDragOver={(e) => onDragOverRow(e, i)}
                onDrop={(e) => onDropRow(e, i)}
                className={`bg-white border rounded-lg shadow-sm overflow-hidden transition-all ${dragIdx === i ? "opacity-40" : ""} ${overIdx === i && dragIdx != null && dragIdx !== i ? "ring-2 ring-[#0F4C81] border-[#0F4C81]" : invalid ? "border-red-400 ring-1 ring-red-300" : "border-gray-300"}`}
                data-testid={`entry-${i}`}>
                <div role="button" tabIndex={0} onClick={() => toggleEntry(i, en)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleEntry(i, en); } }}
                  data-testid={`entry-header-${i}`}
                  className={`flex items-center gap-2 p-3 cursor-pointer transition-colors ${open ? "bg-[#0F4C81]/10 border-b border-gray-200" : "hover:bg-gray-100"}`}>
                  {!readOnly && !heft ? (
                    <span
                      draggable
                      onDragStart={(e) => onDragStart(e, i)}
                      onDragEnd={onDragEnd}
                      onClick={(e) => e.stopPropagation()}
                      title="Zum Verschieben ziehen"
                      data-testid={`entry-drag-${i}`}
                      className="shrink-0 -ml-1 text-gray-400 hover:text-[#0F4C81] cursor-grab active:cursor-grabbing">
                      <GripVertical className="h-4 w-4" />
                    </span>
                  ) : (!readOnly && <span className="shrink-0 -ml-1 w-4" />)}
                  <ChevronRight className={`h-4 w-4 text-gray-600 shrink-0 transition-transform duration-200 ${open ? "rotate-90" : ""}`} />
                  <span className="text-xs font-bold text-[#0F4C81] shrink-0">#{i + 1}</span>
                  <span className="flex-1 min-w-0 truncate text-sm font-semibold text-gray-900">{en.content || en.content_raw || "(ohne Titel)"}</span>
                  {invalid && (
                    <span className="shrink-0 inline-flex items-center text-red-600" data-testid={`entry-invalid-${i}`}
                      title={`Pflichtfelder fehlen: ${miss.map((f) => FIELD_LABEL_DE[f]).join(", ")}`}>
                      <AlertCircle className="h-4 w-4" />
                    </span>
                  )}
                  {(en.page ?? "") !== "" && <span className="text-xs font-semibold text-gray-600 shrink-0">S.&nbsp;{en.page}</span>}
                  {!readOnly && (
                    <span className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(i, -1)} data-testid={`entry-up-${i}`} title="Nach oben verschieben"><ArrowUp className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(i, 1)} data-testid={`entry-down-${i}`} title="Nach unten verschieben"><ArrowDown className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeEntry(i)} data-testid={`entry-del-${i}`} title="Eintrag löschen"><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button>
                    </span>
                  )}
                </div>
                {open && (
                <div className="p-4 space-y-3">
                  <div><Label className="text-xs">Titel</Label>
                    <Textarea value={en.content || ""} onChange={(e) => updateEntry(i, "content", e.target.value)} rows={2} className={`resize-y ${inv("content") ? "field-invalid" : ""}`} data-testid={`entry-content-${i}`} />
                  </div>
                  {isHeftangaben(en) ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div><Label className="text-xs">Datum</Label><Input value={en.date || ""} onChange={(e) => updateEntry(i, "date", e.target.value)} placeholder="YYYY-MM-DD" data-testid={`entry-date-${i}`} /></div>
                      <div><Label className="text-xs">von Seite</Label>
                        <div className="flex items-center gap-1.5">
                          <Input value={en.page ?? ""} onChange={(e) => updateEntry(i, "page", e.target.value)} data-testid={`entry-page-${i}`} />
                          <span role="button" tabIndex={0} title="PDF zu dieser Seite springen"
                            onClick={() => jumpToEntry(en)}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); jumpToEntry(en); } }}
                            data-testid={`entry-jump-${i}`}
                            className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-md border border-gray-400 bg-white text-[#0F4C81] hover:bg-[#0F4C81]/10 cursor-pointer transition-colors">
                            <LocateFixed className="h-4 w-4" />
                          </span>
                        </div>
                      </div>
                      <div><Label className="text-xs">bis Seite</Label><Input value={en.bispage ?? ""} onChange={(e) => updateEntry(i, "bispage", e.target.value)} data-testid={`entry-bispage-${i}`} /></div>
                      <div><Label className="text-xs">Position</Label><Input value={en.position || ""} onChange={(e) => updateEntry(i, "position", e.target.value)} data-testid={`entry-position-${i}`} /></div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-3">
                      <div><Label className="text-xs">von Seite</Label>
                        <div className="flex items-center gap-1.5">
                          <Input value={en.page ?? ""} onChange={(e) => updateEntry(i, "page", e.target.value)} className={inv("page") ? "field-invalid" : ""} data-testid={`entry-page-${i}`} />
                          <span role="button" tabIndex={0} title="PDF zu dieser Seite springen"
                            onClick={() => jumpToEntry(en)}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); jumpToEntry(en); } }}
                            data-testid={`entry-jump-${i}`}
                            className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-md border border-gray-400 bg-white text-[#0F4C81] hover:bg-[#0F4C81]/10 cursor-pointer transition-colors">
                            <LocateFixed className="h-4 w-4" />
                          </span>
                        </div>
                      </div>
                      <div><Label className="text-xs">bis Seite</Label><Input value={en.bispage ?? ""} onChange={(e) => updateEntry(i, "bispage", e.target.value)} className={inv("bispage") ? "field-invalid" : ""} data-testid={`entry-bispage-${i}`} /></div>
                      <div><Label className="text-xs">Position</Label><Input value={en.position || ""} onChange={(e) => updateEntry(i, "position", e.target.value)} className={inv("position") ? "field-invalid" : ""} data-testid={`entry-position-${i}`} /></div>
                    </div>
                  )}
                </div>
                )}
              </div>
              );
            })}
            </div>
            </fieldset>
          </div>

          {/* Echtes PDF (RECHTS, Text markierbar/kopierbar). Auf Mobil/iPad Bild-Viewer (blätterbar). */}
          <div className="shrink-0 h-[80vh] lg:h-full lg:min-h-0 bg-gray-200 lg:border-l border-gray-300" data-testid="pdf-viewer">
            {!isDesktop ? (
              <PagedPdfViewer basePath={`/documents/${docId}`} jump={{ page: pageTarget?.p, nonce: pageTarget?.nonce }} />
            ) : pdfUrl ? (
              <iframe key={pageTarget?.nonce || "base"} title="PDF-Ansicht" src={pdfSrc} className="w-full h-full border-0" data-testid="pdf-frame" />
            ) : pdfError ? (
              <div className="h-full flex items-center justify-center text-center p-6 text-gray-500">
                <div><FileText className="h-6 w-6 mx-auto" /><p className="mt-2 text-xs">PDF nicht verfügbar. Ggf. neu crawlen/hochladen.</p></div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                <div className="text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /><p className="mt-2 text-xs">PDF wird geladen…</p></div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
